import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { media } from 'styled-bootstrap-grid'
import StaticImage from '@components/images/StaticImg'
import IntakeModal from './components/IntakeModal'
import PressMarquee from './components/PressMarquee'
import ProgramFooter from './components/ProgramFooter'
import ProgramNav from './components/ProgramNav'
import TrustBanner from './components/TrustBanner'
import { ROUTES } from './content'
import {
  MARTIN_PORTRAIT,
  martinClose,
  martinFigures,
  martinInterviews,
  martinIntro,
  martinMedia,
  martinTeaching,
  martinVentures,
} from './founderContent'
import { useJumpstartModal } from './jumpstartModal'
import {
  accent,
  AutoGrid,
  Button,
  Card,
  Centered,
  Eyebrow,
  H1,
  H2,
  H3,
  hairline,
  ink,
  Inner,
  Lead,
  muted,
  onDark,
  onDarkAccent,
  PageRoot,
  Pill,
  Section,
  surface,
  Tag,
  TagRow,
} from './styled'

const NAV_LINKS = [
  { href: ROUTES.accelerate, label: 'Accelerate' },
  { href: ROUTES.fullCertification, label: 'Full Certification' },
  { href: ROUTES.letter, label: 'Letter from the founder' },
]

const MENU_LINKS = [{ href: ROUTES.jumpstart, label: 'Jumpstart' }, ...NAV_LINKS]

const Hero = styled.header`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 16px 44px;

  ${media.sm`padding: 56px 20px 56px;`}
  ${media.lg`padding: 68px 24px 72px;`}

  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: center;

  ${media.md`
    grid-template-columns: 300px 1fr;
    gap: 48px;
  `}
`

const Portrait = styled.div`
  max-width: 260px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  background: ${surface};

  ${media.md`max-width: none;`}

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`

const Caption = styled.div`
  margin-top: 10px;
  font-size: 11.5px;
  color: ${muted};
  text-align: center;
`

const Figure = styled.div`
  border-left: 2px solid ${accent};
  padding-left: 16px;
`

const Stat = styled.div`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: #ffffff;

  ${media.sm`font-size: 38px;`}
`

const StatLabel = styled.div`
  margin-top: 6px;
  font-size: 14.5px;
  font-weight: 600;
  color: ${onDarkAccent};
`

const StatNote = styled.div`
  margin-top: 5px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${onDark};
`

const VentureCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #ffffff;
  border: 1px solid ${hairline};
`

const VentureRole = styled(Eyebrow)`
  letter-spacing: 0.08em;
`

const Points = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
`

const Point = styled.li`
  position: relative;
  padding-left: 24px;
  font-size: 15.5px;
  line-height: 1.6;
  color: ${ink};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${accent};
  }
`

// Names rather than logos: these outlets have no logo set and 16 would not fit a marquee.
const MediaBand = styled.div`
  background: ${surface};
  border-top: 1px solid ${hairline};
  border-bottom: 1px solid ${hairline};
  padding: 40px 20px 44px;
`

const BandHeading = styled.div`
  text-align: center;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: ${muted};
  text-transform: uppercase;
  margin-bottom: 8px;
`

const BandNames = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  max-width: 860px;
  margin: 0 auto;
`

const BandName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${ink};
  background: #ffffff;
  border: 1px solid ${hairline};
  padding: 9px 16px;
  border-radius: 999px;
`

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;

  ${media.md`
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  `}
`

const SecondaryLink = styled.a`
  display: inline-block;
  margin-top: 18px;
  font-size: 14.5px;
  font-weight: 600;
`

const AboutMartinPage = ({ onIntakeSubmit }) => {
  const jumpstartModalCopy = useJumpstartModal()
  const [modalOpen, setModalOpen] = useState(false)
  const openModal = useCallback(() => setModalOpen(true), [])
  const closeModal = useCallback(() => setModalOpen(false), [])

  return (
    <PageRoot>
      <TrustBanner />
      <ProgramNav links={NAV_LINKS} menuLinks={MENU_LINKS} cta={{ label: 'Start Jumpstart' }} onCtaClick={openModal} />

      <Hero>
        <div>
          <Portrait>
            <StaticImage src={MARTIN_PORTRAIT} alt="Martin Warner" />
          </Portrait>
          <Caption>{martinIntro.caption}</Caption>
        </div>

        <div>
          <Pill>{martinIntro.eyebrow}</Pill>
          <H1 $gap={20}>{martinIntro.title}</H1>
          <Lead $size="16.5px">{martinIntro.lead}</Lead>
        </div>
      </Hero>

      <PressMarquee />

      <Section $dark>
        <Inner $maxWidth={1000}>
          <AutoGrid $min={210} $gap={28}>
            {martinFigures.map((figure) => (
              <Figure key={figure.label}>
                <Stat>{figure.stat}</Stat>
                <StatLabel>{figure.label}</StatLabel>
                {figure.note && <StatNote>{figure.note}</StatNote>}
              </Figure>
            ))}
          </AutoGrid>
        </Inner>
      </Section>

      <Section>
        <Inner $maxWidth={1000}>
          <Centered $gap={40}>
            <H2 $max={30}>Martin&apos;s current venture focus.</H2>
          </Centered>
          <AutoGrid $min={260} $gap={16}>
            {martinVentures.map((venture) => (
              <VentureCard key={venture.name}>
                <VentureRole>{venture.role}</VentureRole>
                <H3 $size={19} $gap={4}>
                  {venture.name}
                </H3>
                <Lead $size="15px">{venture.body}</Lead>
              </VentureCard>
            ))}
          </AutoGrid>
        </Inner>
      </Section>

      <Section $tinted>
        <Inner $maxWidth={1000}>
          <TwoUp>
            <div>
              <H2 $max={30} $gap={14}>
                {martinTeaching.heading}
              </H2>
              <Lead $size="16px" $margin="0 0 24px">
                {martinTeaching.body}
              </Lead>
            </div>
            <Points>
              {martinTeaching.points.map((point) => (
                <Point key={point}>{point}</Point>
              ))}
            </Points>
          </TwoUp>
        </Inner>
      </Section>

      <Section $tightBottom>
        <Inner $maxWidth={720}>
          <Centered $gap={0} $maxWidth={720}>
            <H2 $max={28} $gap={12}>
              {martinInterviews.heading}
            </H2>
            <Lead $size="15.5px">{martinInterviews.body}</Lead>
            <TagRow style={{ justifyContent: 'center' }}>
              {martinInterviews.names.map((name) => (
                <Tag key={name}>{name}</Tag>
              ))}
            </TagRow>
          </Centered>
        </Inner>
      </Section>

      <MediaBand>
        <BandHeading>{martinMedia.heading.replace('.', '')}</BandHeading>
        <Lead $size="15px" $margin="0 auto 22px" style={{ textAlign: 'center', maxWidth: 560 }}>
          {martinMedia.body}
        </Lead>
        <BandNames>
          {martinMedia.outlets.map((outlet) => (
            <BandName key={outlet}>{outlet}</BandName>
          ))}
        </BandNames>
      </MediaBand>

      <Section $dark>
        <Inner $maxWidth={640}>
          <Centered $gap={0} $maxWidth={640}>
            <H2 $max={30} $gap={14}>
              {martinClose.heading}
            </H2>
            <Lead $onDark $size="16px" $margin="0 0 28px">
              {martinClose.body}
            </Lead>
            <Button type="button" onClick={openModal}>
              Start Jumpstart
            </Button>
            <div>
              <Link href={ROUTES.letter} passHref>
                <SecondaryLink>Read Martin&apos;s letter to founders</SecondaryLink>
              </Link>
            </div>
          </Centered>
        </Inner>
      </Section>

      <ProgramFooter />

      <IntakeModal open={modalOpen} onClose={closeModal} onSubmit={onIntakeSubmit} {...jumpstartModalCopy} />
    </PageRoot>
  )
}

export default AboutMartinPage
